import React, { useState } from 'react';
import styles from './TemplateNamingModal.module.css';
import { TemplateNamingModalProps } from './TemplateNamingModal.types';
import { validateTemplateName } from './TemplateNamingModal.utils';
import { saveFileNameToLocalStorage } from './TemplateNamingModal.service';
import CustomButton from "../../../../reusable_components/custom_button_component/Button";

/**
 * Renders a modal for naming an EL (Entity Logic) template.
 *
 * This component includes:
 * - An input field for entering the template name.
 * - Validation of the entered name before finalizing.
 * - Action buttons for confirming (OK) or canceling the action.
 * - Displays an error message if the entered name is invalid.
 * - Saves the template name as a `.docx` file name in localStorage.
 *
 * @component
 * @param {TemplateNamingModalProps} props - Component props
 * @param {() => void} props.onHide - Callback to close the modal
 * @param {(fileName: string) => void} props.finalizeTemplate - Callback to finalize the template with the given file name
 * @returns {JSX.Element} The rendered modal for naming the template
 */
const TemplateNamingModal: React.FC<TemplateNamingModalProps> = ({ onHide, finalizeTemplate }) => {
  const [name, setName] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleButtonClick = async (result: 'ok' | 'cancel') => {
    if (result === 'ok') {
      const validationError = validateTemplateName(name);
      if (validationError) {
        setError(validationError);
        return;
      }

      const originalFileName = `${name.trim()}.docx`;
      saveFileNameToLocalStorage(originalFileName);
      finalizeTemplate(originalFileName);
    }

    onHide();
  };

  return (
    <div className={styles.namingModal}>
      <div className={`${styles.ascWindow} modal-dlg notransform`}>
        <div className="body">
          <div className={styles.box}>
            <h6 style={{ marginBottom: '5px' }}>
              Enter a name to be used for template
            </h6>
            <div className="input-row">
              <div className="input-field">
                <input
                  type="text"
                  spellCheck="false"
                  className={`form-control ${styles.customInput}`}
                  maxLength={128}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {error && <span className={styles.inputError}>{error}</span>}
              </div>
            </div>
          </div>
          <div className={`flex-row-center-end ${styles.actionButton}`}>
            <CustomButton
              onclick={() => handleButtonClick('ok')}
              content={"Ok"}
            />
            <CustomButton
              onclick={() => handleButtonClick('cancel')}
              content={"Cancel"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateNamingModal;
